import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useTheme } from '~/hooks/useTheme'
import { createEventsServicePlugin } from '@schedule-x/events-service'
import { createEventModalPlugin } from '@schedule-x/event-modal'
import {
  createViewDay,
  createViewMonthAgenda,
  createViewMonthGrid,
  createViewWeek,
} from '@schedule-x/calendar'
import { createCalendarControlsPlugin } from '@schedule-x/calendar-controls'
import { useCalendarApp, ScheduleXCalendar } from '@schedule-x/react'
import { DynamicCalendarStyles } from '../DynamicCalendarStyles'
import type { FilterArrayProps } from '~/components/CrudIndex/components/FiltersDashboard'
import { calendarWorkSchedule } from '../../services'
import type { WorkScheduleCalendarResponse } from '../../interfaces'
import { mapWorkScheduleListToCalendarEvents } from '../../utils/mappers'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { customComponents } from '../EventModal'
dayjs.extend(isoWeek)

interface WorkScheduleCalendarProps {
  readonly filters?: FilterArrayProps[],
  readonly forceRefresh?: boolean
}
interface DateRange {
  readonly start_date: string;
  readonly finish_date: string;
}

// eslint-disable-next-line max-lines-per-function
export default function WorkScheduleCalendar({ filters, forceRefresh }: WorkScheduleCalendarProps) {
  const { darkMode } = useTheme()

  const eventsService = useMemo(() => createEventsServicePlugin(), [])
  const eventModal = useMemo(() => createEventModalPlugin(), [])
  const calendarControls = useMemo(() => createCalendarControlsPlugin(), [])

  const [currentView, setCurrentView] = useState<string>('month-grid');
  const abortControllerRef = useRef<AbortController | null>(null);
  const isCalendarInitialized = useRef(false);
  const currentViewRef = useRef<string>('month-grid');
  const lastRequestRef = useRef<string>('');

  const customComponentsWithView = useMemo(() => ({
    eventModal: ({ calendarEvent }) => customComponents.eventModal({ calendarEvent, currentView }),
    timeGridEvent: ({ calendarEvent }) => customComponents.timeGridEvent({ calendarEvent, currentView }),
  }), [currentView]);

  function calculateDateRange(viewMode: string, referenceDate: Temporal.PlainDate): DateRange {
    const dateString = referenceDate.toString().split('[')[0];
    const date = dayjs(dateString);

    switch (viewMode) {
      case 'day':
        return {
          start_date: date.startOf('day').toISOString(),
          finish_date: date.endOf('day').toISOString()
        };
      case 'week':
        return {
          start_date: date.startOf('isoWeek').toISOString(),
          finish_date: date.endOf('isoWeek').toISOString()
        };
      default:
        return {
          start_date: date.startOf('month').toISOString(),
          finish_date: date.endOf('month').toISOString()
        };
    }
  }

  const syncFiltersWithDateRange = (dateRange: DateRange) => {
    const startDateFilter = filters?.find(filter => filter.name === 'start_date' || filter.type === 'start_date');
    const finishDateFilter = filters?.find(filter => filter.name === 'finish_date' || filter.type === 'finish_date');

    if (startDateFilter) {
      startDateFilter.variable.set(dateRange.start_date);
    }

    if (finishDateFilter) {
      finishDateFilter.variable.set(dateRange.finish_date);
    }
  };

  const clearEvents = () => {
    if (!isCalendarInitialized.current || !eventsService?.getAll) {
      return;
    }

    try {
      const allEvents = eventsService.getAll();
      allEvents.forEach(event => {
        eventsService.remove(event.id);
      });
    } catch (error) {
      console.warn('Error clearing events:', error);
    }
  }

  const loadEvents = async (dateRange: DateRange) => {
    const requestId = `${dateRange.start_date}-${dateRange.finish_date}`;

    if (lastRequestRef.current === requestId) {
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    lastRequestRef.current = requestId;

    abortControllerRef.current = new AbortController();
    const currentController = abortControllerRef.current;

    clearEvents();

    const objFilters = {
      ...filters?.reduce((obj, item) => {
        obj[item.name ? item.name : item.type] = item.variable.get();
        return obj;
      }, {}),
      start_date: dateRange.start_date,
      finish_date: dateRange.finish_date
    };


    try {
      const data: WorkScheduleCalendarResponse[] = await calendarWorkSchedule({
        filters: objFilters,
        signal: currentController.signal
      });

      if (currentController.signal.aborted) {
        return;
      }

      if (lastRequestRef.current !== requestId) {
        return;
      }

      if (data.length && isCalendarInitialized.current) {
        const formattedEvents = mapWorkScheduleListToCalendarEvents({
          eventCalendar: data,
          viewMode: currentViewRef.current
        });

        formattedEvents.forEach(event => {
          eventsService.add(event);
        });
      }
    } catch (error) {
      if (!currentController.signal.aborted) {
        console.error('Error loading events:', error);
      }
    }
  };

  const calendar = useCalendarApp({
    views: [
      createViewDay(),
      createViewWeek(),
      createViewMonthAgenda(),
      createViewMonthGrid(),
    ],
    defaultView: createViewMonthGrid().name,
    firstDayOfWeek: 1,
    locale: 'pt-BR',
    timezone: 'America/Sao_Paulo',
    selectedDate: Temporal.Now.plainDateISO(),
    plugins: [
      eventsService,
      eventModal,
      calendarControls,
    ],
    callbacks: {
      onEventClick(calendarEvent, event: UIEvent) {
      },
      onRangeUpdate: ({ start, end }) => {
        const view = calendarControls.getView();
        const date = calendarControls.getDate();

        currentViewRef.current = view;
        setCurrentView(view);

        const dateRange = calculateDateRange(view, date);

        syncFiltersWithDateRange(dateRange);

        loadEvents(dateRange);
      },
    },
    events: [],
  })

  useEffect(() => {
    isCalendarInitialized.current = true;

    return () => {
      isCalendarInitialized.current = false;
    };
  }, []);

  const syncAndReloadEvents = () => {
    const view = calendarControls.getView();
    const date = calendarControls.getDate();
    const dateRange = calculateDateRange(view, date);

    syncFiltersWithDateRange(dateRange);

    lastRequestRef.current = '';

    loadEvents(dateRange);
  }

  useEffect(() => {
    syncAndReloadEvents();
  }, [forceRefresh]);

  useEffect(() => {
    return () => {
      clearEvents();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <DynamicCalendarStyles darkMode={darkMode} />
      <ScheduleXCalendar
        customComponents={customComponentsWithView}
        calendarApp={calendar}
      />
    </>
  )
}
